import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DragAndDropIcon from 'components/atoms/icons/dragAndDropIcon';
import LockIcon from 'components/atoms/icons/lockIcon';

import s from './styles.module.scss';

import type { ChangeEventHandler, DragEventHandler, FC } from 'react';

interface Props {
  onUpload: (file: File[]) => void;
  fileType: 'csv' | 'json';
  sampleFileUrl?: string;
  disabled: boolean;
  multiFile?: boolean;
}

const FILE_SIZE_LIMIT = 10485760; // in bytes

const UploadFile: FC<Props> = ({
  onUpload,
  fileType,
  sampleFileUrl,
  disabled,
  multiFile = false,
}) => {
  const { t } = useTranslation();
  const [dropping, setDropping] = useState(false);
  const [uploadError, setUploadError] = useState<null | string>(null);

  const validateFile = (file: File): string | undefined => {
    if (!file.type.includes(fileType)) {
      return t('labels.fileTypeNotAllowed', { fileType: file.type });
    }

    if (file.size === 0) {
      return t('labels.fileIsEmpty');
    }

    if (file.size > FILE_SIZE_LIMIT) {
      return t('labels.fileExceedsLimitation');
    }
    return undefined;
  };

  const handleFile = async (files: FileList) => {
    if (disabled) return;
    setDropping(false);
    setUploadError(null);
    const filesArray = Array.from(files);
    let isValid = true;
    filesArray.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        isValid = false;
      }
    });
    if (isValid) {
      onUpload(filesArray);
    }
  };

  const handleDrag: DragEventHandler<HTMLElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDropping(true);
    } else if (e.type === 'dragleave') {
      setDropping(false);
    }
  };

  const handleDrop: DragEventHandler<HTMLElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    handleFile(e.dataTransfer.files);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    if (e.target.files) {
      handleFile(e.target.files);
      e.target.value = '';
    }
  };

  return (
    <div className="admin-csv-upload">
      <form
        id="form-file-upload"
        encType="multipart/form-data"
        onDragEnter={handleDrag}
        onSubmit={(e) => e.preventDefault()}
      >
        <input
          name="file"
          type="file"
          id="input-file-upload"
          accept={`.${fileType}`}
          onChange={handleChange}
          disabled={disabled}
          multiple={multiFile}
        />
        <label
          id="label-file-upload"
          htmlFor="input-file-upload"
          className={dropping ? 'drag-active' : s['input-file-upload']}
        >
          {disabled ? <LockIcon /> : <DragAndDropIcon />}
          <p className="title">
            {t('labels.dragDropOrChooseFile')}{' '}
            <span className={disabled ? '' : 'choose-active'}>
              {t('labels.chooseFile')}
            </span>{' '}
            {t('labels.toUpload')}
          </p>
          <p className="text">
            {t('labels.fileShouldBeInFormat', {
              fileType: fileType.toUpperCase(),
            })}
            {multiFile ? '' : ` ${t('labels.oneFile')}`} {t('labels.upTo10Mb')}
            <br />
            {sampleFileUrl && <span>{t('labels.fileTemplateMustBeSame')}</span>}
          </p>
          {uploadError && <p className="error-text">{uploadError}</p>}
        </label>
        {dropping && (
          <div
            id="drag-file-element"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        )}
      </form>
    </div>
  );
};

export default UploadFile;
